import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import {
  Shield,
  Wallet,
  TrendingUp,
  FileText,
  AlertCircle,
  Clock,
  CheckCircle2,
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import VPFManagement from '../vpf/VPFManagement';
import {
  fetchMyPFAccount,
  fetchMyPFLedger,
  fetchMyPFAdvances,
  fetchMyPFRecovery,
  submitPFAdvanceRequest
} from '../../services/pfAccountApi';
import { formatDate } from '../../utils/dateUtils';

const MyPFManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // State
  const [account, setAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [advances, setAdvances] = useState([]);
  const [recoveryInfo, setRecoveryInfo] = useState(null);
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  // Dialog State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requestType, setRequestType] = useState('REFUNDABLE');
  const [requestedAmount, setRequestedAmount] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const currentMaxEligible = requestType === 'REFUNDABLE'
    ? (account?.eligible_refundable_amount || 0)
    : (account?.eligible_non_refundable_amount || 0);

  const parsedAmount = parseFloat(requestedAmount);
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= currentMaxEligible;

  const handleTypeChange = (newType) => {
    setRequestType(newType);
    const newMax = newType === 'REFUNDABLE'
      ? (account?.eligible_refundable_amount || 0)
      : (account?.eligible_non_refundable_amount || 0);
    setRequestedAmount(newMax > 0 ? String(newMax) : '');
    setModalError('');
  };

  const handleOpenModal = () => {
    setRequestType('REFUNDABLE');
    const defaultAmount = account?.eligible_refundable_amount || 0;
    setRequestedAmount(defaultAmount > 0 ? String(defaultAmount) : '');
    setReason('');
    setRemarks('');
    setModalError('');
    setIsModalOpen(true);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    setError('');
    try {
      const [acc, adv, rec, leg] = await Promise.all([
        fetchMyPFAccount(),
        fetchMyPFAdvances(),
        fetchMyPFRecovery(),
        fetchMyPFLedger(1, 20)
      ]);
      setAccount(acc);
      setAdvances(adv);
      setRecoveryInfo(rec);
      setLedgerData(leg);
    } catch (err) {
      setError(err.message || 'Failed to load PF data.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newPage) => {
    try {
      const leg = await fetchMyPFLedger(newPage, ledgerData.page_size);
      setLedgerData(leg);
    } catch (err) {
      setError(err.message || 'Failed to load ledger page.');
    }
  };

  const handleSubmitAdvance = async (e) => {
    e.preventDefault();
    setModalError('');

    const amt = parseFloat(requestedAmount);
    if (isNaN(amt) || amt <= 0) {
      setModalError('Requested amount must be greater than zero.');
      return;
    }
    if (amt > currentMaxEligible) {
      setModalError(`Requested amount (₹${amt}) cannot exceed maximum eligible limit of ₹${currentMaxEligible} (${requestType === 'REFUNDABLE' ? '50%' : '75%'} cap).`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        request_type: requestType,
        requested_amount: amt,
        reason: reason.trim(),
        remarks: remarks.trim() || undefined
      };
      await submitPFAdvanceRequest(payload);
      setSuccess('PF Advance request submitted successfully.');
      setIsModalOpen(false);
      loadAllData();
    } catch (err) {
      setModalError(err.message || 'Failed to submit PF advance request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !account) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        <Clock className="w-6 h-6 animate-spin mr-3 text-blue-600" />
        <span>Loading PF Account details...</span>
      </div>
    );
  }

  const formatMoney = (val) =>
    `₹${Math.round(Number(val || 0)).toLocaleString('en-IN')}`;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-slate-800 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl">
                <Shield className="w-8 h-8 text-blue-200" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">My PF Subsystem</h1>
                <p className="text-blue-100 text-sm mt-1">
                  Contributory Provident Fund, VPF, Advance Requests & Passbook Audit Ledger
                </p>
              </div>
            </div>
          </div>
          {account && (
            <div className="bg-white/10 backdrop-blur-md px-5 py-3 rounded-xl border border-white/20 text-right">
              <p className="text-xs text-blue-200 uppercase font-semibold tracking-wider">Total PF Balance</p>
              <p className="text-2xl font-black text-white">{formatMoney(account.current_pf_balance)}</p>
            </div>
          )}
        </div>

        {/* Sub Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mt-6 border-t border-white/10 pt-4">
          {[
            { id: 'overview', label: 'Overview', icon: Wallet },
            { id: 'vpf', label: 'VPF', icon: TrendingUp },
            { id: 'advance', label: 'PF Advance', icon: Shield },
            { id: 'ledger', label: 'PF Ledger / Passbook', icon: FileText }
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  active
                    ? 'bg-white text-blue-900 shadow-md scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && account && (
        <div className="space-y-6">
          {/* Informational banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3 text-blue-800 text-sm">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">PF Balance Rule:</span> Total PF Balance is the sum of your employee PFS contributions, VPF contributions, and PF Advance recoveries minus sanctioned advances. <strong>CPF contributions are payroll deductions and do not form part of your personal PF Account balance or advance eligibility.</strong>
            </div>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total PF Balance</p>
              <p className="text-2xl font-extrabold text-blue-900 mt-2">{formatMoney(account.current_pf_balance)}</p>
              {account.current_pf_balance_as_on && (
                <p className="text-[10px] text-blue-600 mt-1">Balance as on: {formatDate(account.current_pf_balance_as_on)}</p>
              )}
              <p className="text-xs text-blue-600 font-medium mt-1">Source of Truth Account</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee Contribution (PFS)</p>
              <p className="text-2xl font-extrabold text-gray-900 mt-2">{formatMoney(account.employee_contribution)}</p>
              {account.pfs_contribution_as_on && (
                <p className="text-[10px] text-gray-500 mt-1">Contribution as on: {formatDate(account.pfs_contribution_as_on)}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">PFS + Recoveries</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">VPF Balance</p>
              <p className="text-2xl font-extrabold text-purple-900 mt-2">{formatMoney(account.vpf_balance)}</p>
              {account.vpf_balance_as_on && (
                <p className="text-[10px] text-purple-600 mt-1">Balance as on: {formatDate(account.vpf_balance_as_on)}</p>
              )}
              <p className="text-xs text-purple-600 font-medium mt-1">Voluntary Contributions</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">PF Advance Recovered</p>
              <p className="text-2xl font-extrabold text-green-700 mt-2">{formatMoney(account.total_pf_advance_recovered || 0)}</p>
              {account.pf_advance_recovered_as_on && (
                <p className="text-[10px] text-green-600 mt-1">Recovered as on: {formatDate(account.pf_advance_recovered_as_on)}</p>
              )}
              <p className="text-xs text-green-600 font-medium mt-1">Cumulative Recoveries</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Outstanding Refundable Advance</p>
              <p className="text-2xl font-extrabold text-amber-600 mt-2">{formatMoney(account.outstanding_refundable_advance)}</p>
              {account.outstanding_refundable_as_on && (
                <p className="text-[10px] text-amber-600 mt-1">Outstanding as on: {formatDate(account.outstanding_refundable_as_on)}</p>
              )}
              <p className="text-xs text-amber-700 font-medium mt-1">Pending Recovery</p>
            </div>
          </div>

          {/* Advance Eligibility Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 p-6 rounded-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-indigo-900">Refundable PF Advance Eligibility</h3>
                  <p className="text-xs text-indigo-600 mt-1">Max 50% of current PF balance. Recovered in 10 installments.</p>
                </div>
                <span className="px-3 py-1 bg-indigo-200 text-indigo-800 text-xs font-bold rounded-full">50% Cap</span>
              </div>
              <div className="mt-4 pt-4 border-t border-indigo-100 flex justify-between items-end">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Eligible Amount</p>
                  <p className="text-2xl font-extrabold text-indigo-900">{formatMoney(account.eligible_refundable_amount)}</p>
                </div>
                {/* Apply Refundable Button is hidden (Admin/HR control only) */}
              </div>
              {!account.can_apply_refundable && (
                <p className="text-xs text-red-600 mt-3 font-medium flex items-center">
                  <AlertCircle className="w-3.5 h-3.5 mr-1" />
                  {account.refundable_apply_restriction}
                </p>
              )}
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-6 rounded-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-emerald-900">Non-Refundable PF Advance Eligibility</h3>
                  <p className="text-xs text-emerald-600 mt-1">Max 75% of current PF balance. No payroll recovery required.</p>
                </div>
                <span className="px-3 py-1 bg-emerald-200 text-emerald-800 text-xs font-bold rounded-full">75% Cap</span>
              </div>
              <div className="mt-4 pt-4 border-t border-emerald-100 flex justify-between items-end">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Eligible Amount</p>
                  <p className="text-2xl font-extrabold text-emerald-900">{formatMoney(account.eligible_non_refundable_amount)}</p>
                </div>
                {/* Apply Non-Refundable Button is hidden (Admin/HR control only) */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: VPF */}
      {activeTab === 'vpf' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <VPFManagement />
        </div>
      )}

      {/* TAB 3: PF ADVANCE */}
      {activeTab === 'advance' && (
        <div className="space-y-6">
          {/* Action Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="text-lg font-bold text-gray-900">PF Advance Applications & Recovery</h3>
              <p className="text-xs text-gray-500 mt-0.5">Apply for refundable or non-refundable advances and track recovery schedules.</p>
            </div>
            {/* Apply Button is hidden (Admin/HR control only) */}
          </div>

          {/* Active Recovery Progress Card */}
          {recoveryInfo && recoveryInfo.has_active_recovery && recoveryInfo.recovery && (
            <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white rounded-2xl p-6 shadow-lg border border-blue-900">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full border border-amber-500/30">
                    Active Refundable Recovery Schedule
                  </span>
                  <h4 className="text-xl font-bold mt-2">Monthly Installment: {formatMoney(recoveryInfo.recovery.monthly_installment)}</h4>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Outstanding Balance</p>
                  <p className="text-2xl font-extrabold text-amber-400">{formatMoney(recoveryInfo.recovery.outstanding_amount)}</p>
                </div>
              </div>

              {/* Visual Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-300 font-medium">
                  <span>Progress: {recoveryInfo.recovery.installments_paid} / {recoveryInfo.recovery.total_installments} Installments Paid</span>
                  <span>{formatMoney(recoveryInfo.recovery.recovered_amount)} / {formatMoney(recoveryInfo.recovery.approved_amount)}</span>
                </div>
                <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden p-0.5 border border-white/20">
                  <div
                    className="bg-gradient-to-r from-blue-400 to-emerald-400 h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, (recoveryInfo.recovery.installments_paid / recoveryInfo.recovery.total_installments) * 100)}%`
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {recoveryInfo && !recoveryInfo.has_active_recovery && (
            <div className="bg-gray-50 border border-gray-200 text-gray-500 rounded-xl p-4 text-xs font-medium">
              No active refundable PF advance recovery schedule.
            </div>
          )}

          {/* Advances History Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h4 className="font-bold text-gray-900">Advance Request History</h4>
              <span className="text-xs text-gray-500">{advances.length} Request(s)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-6 py-3">Request Date</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Requested</th>
                    <th className="px-6 py-3">Eligible</th>
                    <th className="px-6 py-3">Approved</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {advances.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                        No PF advance requests submitted yet.
                      </td>
                    </tr>
                  ) : (
                    advances.map((adv) => (
                      <tr key={adv.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {formatDate(adv.created_at, '-')}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                            adv.request_type === 'REFUNDABLE' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {adv.request_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-900">{formatMoney(adv.requested_amount)}</td>
                        <td className="px-6 py-4 text-gray-600">{formatMoney(adv.eligible_amount)}</td>
                        <td className="px-6 py-4 font-bold text-blue-700">
                          {adv.approved_amount ? formatMoney(adv.approved_amount) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                            adv.status === 'RECOVERING' || adv.status === 'IN_RECOVERY' ? 'bg-blue-100 text-blue-800' :
                            adv.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            adv.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' :
                            adv.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                            adv.status === 'CANCELLED' ? 'bg-gray-100 text-gray-600' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {adv.status === 'RECOVERING' || adv.status === 'IN_RECOVERY' ? 'RECOVERING' : adv.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 max-w-xs truncate">{adv.reason || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: PF LEDGER / PASSBOOK */}
      {activeTab === 'ledger' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm space-y-4">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div>
              <h4 className="font-bold text-gray-900">PF Passbook Audit Ledger</h4>
              <p className="text-xs text-gray-500">Immutable credit/debit audit trail for your PF account.</p>
            </div>
            <span className="text-xs font-semibold px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
              Total Transactions: {ledgerData.total}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Remarks</th>
                  <th className="px-6 py-3 text-right">Credit</th>
                  <th className="px-6 py-3 text-right">Debit</th>
                  <th className="px-6 py-3 text-right"> Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ledgerData.items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                      No ledger transactions recorded yet.
                    </td>
                  </tr>
                ) : (
                  ledgerData.items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-xs font-medium text-gray-600">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          row.transaction_type === 'CONTRIBUTION' ? 'bg-blue-100 text-blue-800' :
                          row.transaction_type === 'VPF' ? 'bg-purple-100 text-purple-800' :
                          row.transaction_type === 'ADVANCE_RECOVERY' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {row.transaction_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-600 max-w-sm truncate">{row.remarks || '-'}</td>
                      <td className="px-6 py-4 text-right font-bold text-emerald-600">
                        {row.credit > 0 ? `+${formatMoney(row.credit)}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-red-600">
                        {row.debit > 0 ? `-${formatMoney(row.debit)}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-black text-gray-900">{formatMoney(row.running_balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {ledgerData.total > ledgerData.page_size && (
            <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
              <button
                disabled={ledgerData.page <= 1}
                onClick={() => handlePageChange(ledgerData.page - 1)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-xs font-bold rounded-lg transition"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>
              <span className="text-xs text-gray-500 font-semibold">
                Page {ledgerData.page} of {Math.ceil(ledgerData.total / ledgerData.page_size)}
              </span>
              <button
                disabled={ledgerData.page * ledgerData.page_size >= ledgerData.total}
                onClick={() => handlePageChange(ledgerData.page + 1)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-xs font-bold rounded-lg transition"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* APPLY ADVANCE MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl animate-fade-in border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-lg font-bold text-gray-900">Apply for PF Advance</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold text-lg">×</button>
            </div>

            {modalError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSubmitAdvance} className="space-y-4">
              {/* Type Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Advance Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleTypeChange('REFUNDABLE')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      requestType === 'REFUNDABLE' ? 'border-indigo-600 bg-indigo-50 text-indigo-900 ring-2 ring-indigo-200' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Refundable</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Max 50% Cap • 10 Monthly Installments</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTypeChange('NON_REFUNDABLE')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      requestType === 'NON_REFUNDABLE' ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-200' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Non-Refundable</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Max 75% Cap • No Payroll Recovery</p>
                  </button>
                </div>
              </div>

              {/* Max Cap Info Box */}
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs flex justify-between items-center">
                <div>
                  <span className="text-gray-600 font-bold">
                    Eligible PF Advance Limit ({requestType === 'REFUNDABLE' ? '50% Cap' : '75% Cap'}):
                  </span>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {requestType === 'REFUNDABLE'
                      ? 'Maximum calculated as 50% of current PF balance (rounded to ₹10)'
                      : 'Maximum calculated as 75% of current PF balance'}
                  </p>
                </div>
                <span className="font-extrabold text-blue-900 text-sm">
                  {formatMoney(currentMaxEligible)}
                </span>
              </div>

              {/* Active Recovery Block Warning */}
              {requestType === 'REFUNDABLE' && !account?.can_apply_refundable && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs">
                  <strong>Restriction:</strong> {account?.refundable_apply_restriction}
                </div>
              )}

              {/* Editable Amount Input */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-gray-700 uppercase">
                    Requested PF Advance Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  {currentMaxEligible > 0 && (
                    <button
                      type="button"
                      onClick={() => setRequestedAmount(String(currentMaxEligible))}
                      className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition"
                    >
                      Set to Max ({formatMoney(currentMaxEligible)})
                    </button>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 font-bold text-sm">
                    ₹
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max={currentMaxEligible}
                    required
                    value={requestedAmount}
                    onChange={(e) => setRequestedAmount(e.target.value)}
                    placeholder={`Enter amount up to ${formatMoney(currentMaxEligible)}`}
                    className={`w-full pl-8 pr-3 py-2 border rounded-xl text-sm font-semibold outline-none transition ${
                      !requestedAmount || isNaN(parsedAmount) || parsedAmount <= 0
                        ? 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                        : parsedAmount > currentMaxEligible
                        ? 'border-red-500 bg-red-50/50 text-red-900 focus:ring-2 focus:ring-red-400'
                        : 'border-emerald-500 bg-emerald-50/30 text-gray-900 focus:ring-2 focus:ring-emerald-400'
                    }`}
                  />
                </div>
                {requestedAmount && !isNaN(parsedAmount) && parsedAmount > currentMaxEligible && (
                  <p className="text-xs text-red-600 font-medium mt-1 flex items-center">
                    <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    Requested amount exceeds maximum eligible limit of {formatMoney(currentMaxEligible)} ({requestType === 'REFUNDABLE' ? '50%' : '75%'} cap).
                  </p>
                )}
                {requestedAmount && !isNaN(parsedAmount) && parsedAmount <= 0 && (
                  <p className="text-xs text-red-600 font-medium mt-1 flex items-center">
                    <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    Requested amount must be greater than zero.
                  </p>
                )}
                {isAmountValid && (
                  <p className="text-[11px] text-emerald-700 font-medium mt-1">
                    ✓ Valid request amount ({formatMoney(parsedAmount)} within {formatMoney(currentMaxEligible)} limit)
                  </p>
                )}
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Reason</label>
                <textarea
                  required
                  rows="2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Specify reason for advance (e.g. Medical emergency, marriage, education)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !isAmountValid || (requestType === 'REFUNDABLE' && !account?.can_apply_refundable)}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-sm transition"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPFManagement;
